/**
 * Dynamic Shipping Badge System
 * 
 * Features:
 * - Real-time cart monitoring
 * - Dynamic free shipping threshold updates
 * - Progressive messaging (countdown to free shipping)
 * - Visual state changes (red/green badge system)
 * - Shopify cart integration
 */

class DynamicShippingBadge {
  constructor() {
    this.badges = document.querySelectorAll('.dynamic-shipping-badge');
    this.freeShippingThreshold = 60000; // 600 TL in cents (600 * 100)
    this.currency = 'TRY';
    this.currentCartTotal = 0;
    
    // Initialize if badges exist
    if (this.badges.length > 0) {
      this.init();
    }
  }

  init() {
    this.setupEventListeners();
    this.getCurrentCartTotal();
    this.updateAllBadges();
    
    console.log('Dynamic Shipping Badge System initialized');
  }

  setupEventListeners() {
    // Listen for cart updates
    document.addEventListener('cart:updated', (event) => {
      this.handleCartUpdate(event.detail);
    });

    // Listen for variant changes (product pages)
    document.addEventListener('variant:change', (event) => {
      this.handleVariantChange(event.detail);
    });

    // Listen for quantity changes
    document.addEventListener('change', (event) => {
      if (event.target.matches('[data-quantity-input]')) {
        this.handleQuantityChange(event.target);
      }
    });

    // Listen for add to cart events
    document.addEventListener('product:added-to-cart', (event) => {
      this.handleProductAdded(event.detail);
    });

    // Listen for Shopify cart drawer updates
    document.addEventListener('cart-drawer:updated', () => {
      setTimeout(() => this.getCurrentCartTotal(), 100);
    });

    // Listen for AJAX cart updates (for themes using AJAX cart)
    document.addEventListener('cart:change', (event) => {
      this.handleCartUpdate(event.detail);
    });
  }

  async getCurrentCartTotal() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      this.currentCartTotal = cart.total_price || 0;
      this.updateAllBadges();
      
      console.log(`Cart total: ${this.currentCartTotal} cents (${this.formatMoney(this.currentCartTotal)})`);
    } catch (error) {
      console.error('Error fetching cart data:', error);
      // Fallback: try to get cart total from theme global
      if (window.theme && window.theme.CartAPI) {
        this.currentCartTotal = window.theme.CartAPI.getCartTotal() || 0;
        this.updateAllBadges();
      }
    }
  }

  handleCartUpdate(cartData) {
    if (cartData && typeof cartData.total_price !== 'undefined') {
      this.currentCartTotal = cartData.total_price;
      this.updateAllBadges();
    } else {
      // Fallback: refetch cart data
      this.getCurrentCartTotal();
    }
  }

  handleVariantChange(variantData) {
    // When variant changes, we might need to update based on potential cart total
    // This is more for preview purposes on product pages
    if (variantData && variantData.price) {
      this.updateAllBadges();
    }
  }

  handleQuantityChange(quantityInput) {
    // Debounce quantity changes to avoid too many updates
    clearTimeout(this.quantityUpdateTimeout);
    this.quantityUpdateTimeout = setTimeout(() => {
      this.getCurrentCartTotal();
    }, 500);
  }

  handleProductAdded(productData) {
    // When a product is added to cart, update the cart total
    setTimeout(() => {
      this.getCurrentCartTotal();
    }, 300);
  }

  updateAllBadges() {
    this.badges.forEach(badge => {
      this.updateBadge(badge);
    });
  }

  updateBadge(badgeElement) {
    const shippingBadge = badgeElement.querySelector('[data-shipping-status]');
    const messageElement = badgeElement.querySelector('[data-shipping-message]');
    const progressElement = badgeElement.querySelector('[data-shipping-progress]');
    
    if (!shippingBadge || !messageElement) return;

    const remaining = this.freeShippingThreshold - this.currentCartTotal;
    
    if (this.currentCartTotal >= this.freeShippingThreshold) {
      // Free shipping achieved
      this.setBadgeComplete(shippingBadge, messageElement, progressElement);
    } else if (this.currentCartTotal > 0) {
      // Cart has items but hasn't reached threshold
      this.setBadgeProgress(shippingBadge, messageElement, progressElement, remaining);
    } else {
      // Empty cart
      this.setBadgeIncomplete(shippingBadge, messageElement, progressElement);
    }
  }

  setBadgeComplete(badge, messageEl, progressEl) {
    badge.className = 'shipping-badge shipping-badge--complete';
    messageEl.textContent = 'Kargo bedava! 🎉';
    
    if (progressEl) {
      progressEl.style.display = 'none';
    }

    // Add success animation
    badge.style.animation = 'successGlow 1.5s ease-in-out';
    setTimeout(() => {
      badge.style.animation = '';
    }, 1500);
  }

  setBadgeProgress(badge, messageEl, progressEl, remaining) {
    badge.className = 'shipping-badge shipping-badge--progress';
    
    const remainingFormatted = this.formatMoney(remaining);
    messageEl.textContent = `${remainingFormatted} daha ekleyin, kargo bedava!`;
    
    if (progressEl) {
      const progressPercent = ((this.currentCartTotal / this.freeShippingThreshold) * 100).toFixed(0);
      progressEl.textContent = `%${progressPercent} tamamlandı`;
      progressEl.style.display = 'inline';
    }
  }

  setBadgeIncomplete(badge, messageEl, progressEl) {
    badge.className = 'shipping-badge shipping-badge--incomplete';
    messageEl.textContent = '600 TL üzeri siparişinizde kargo bedava!';
    
    if (progressEl) {
      progressEl.style.display = 'none';
    }
  }

  formatMoney(cents) {
    // Convert cents to TRY and format
    const amount = cents / 100;
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  // Public method to manually update badges (for external integrations)
  refresh() {
    this.getCurrentCartTotal();
  }

  // Public method to set custom threshold (for dynamic configuration)
  setThreshold(newThreshold) {
    this.freeShippingThreshold = newThreshold;
    this.updateAllBadges();
  }
}

// Initialize when DOM is ready
function initDynamicShippingBadge() {
  if (typeof window.DynamicShippingBadge === 'undefined') {
    window.DynamicShippingBadge = new DynamicShippingBadge();
  }
}

// Multiple initialization methods for different theme setups
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDynamicShippingBadge);
} else {
  initDynamicShippingBadge();
}

// Also initialize on Shopify theme events
document.addEventListener('shopify:section:load', initDynamicShippingBadge);
document.addEventListener('shopify:section:reorder', initDynamicShippingBadge);

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DynamicShippingBadge;
}

/**
 * Integration with Shopify AJAX Cart API
 * This section provides compatibility with various cart systems
 */

// Listen for native Shopify cart events
document.addEventListener('cart:updated', function(event) {
  if (window.DynamicShippingBadge) {
    window.DynamicShippingBadge.refresh();
  }
});

// Integration with common theme cart APIs
if (typeof window.CartAPI !== 'undefined') {
  const originalUpdateCart = window.CartAPI.updateCart;
  window.CartAPI.updateCart = function(...args) {
    const result = originalUpdateCart.apply(this, args);
    if (window.DynamicShippingBadge) {
      setTimeout(() => window.DynamicShippingBadge.refresh(), 100);
    }
    return result;
  };
}

// Integration with Shopify Plus checkout
if (window.Shopify && window.Shopify.theme) {
  document.addEventListener('theme:cart:change', function() {
    if (window.DynamicShippingBadge) {
      window.DynamicShippingBadge.refresh();
    }
  });
}

console.log('Dynamic Shipping Badge System loaded');
